<script setup lang="ts">
/**
 * RichTextViewer —— 共享 `<RichText>` 的前台包装（F25）。
 *
 * 共享 RichText 直接注入净化后的 HTML 字符串，无法对内部 img 做模板级
 * 事件绑定，因此在根元素上做事件委托：点击命中 IMG 时打开 ImageLightbox
 * 全屏查看（img 被 `<a>` 包裹时灯箱优先于跳链）；点击非图片内容不响应。
 * props 与共享 RichText 完全对齐，消费点换标签即可，无样式侵入。
 */
import { nextTick, onMounted, ref, watch } from 'vue';
import { RichText } from '@edu/shared/richtext';

import ImageLightbox from '@/components/ImageLightbox.vue';
import { markStandaloneLinkCards } from '@/composables/link-card-marker';

const props = defineProps<{
  readonly html?: string | null;
  readonly as?: string;
  readonly previewToken?: string | null;
  readonly pdfCacheVersion?: string | null;
}>();

interface LightboxTarget {
  readonly src: string;
  readonly alt: string;
}

const richTextRef = ref<InstanceType<typeof RichText> | null>(null);

/** 共享 RichText 的 HTML 注入在组件重渲染时才落地，post flush 保证标记打在新 DOM 上。 */
function applyLinkCardMarkers(): void {
  const el = richTextRef.value?.$el;
  if (el instanceof HTMLElement) {
    markStandaloneLinkCards(el);
  }
}

onMounted(() => {
  void nextTick(applyLinkCardMarkers);
});
watch(() => props.html, applyLinkCardMarkers, { flush: 'post' });

const lightboxTarget = ref<LightboxTarget | null>(null);

function onRootClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof HTMLImageElement)) {
    return;
  }
  if (target.closest('a')) {
    event.preventDefault();
  }
  lightboxTarget.value = {
    src: target.currentSrc || target.src,
    alt: target.alt,
  };
}

function closeLightbox(): void {
  lightboxTarget.value = null;
}
</script>

<template>
  <RichText
    ref="richTextRef"
    :html="html"
    :as="as"
    :preview-token="previewToken"
    :pdf-cache-version="pdfCacheVersion"
    @click="onRootClick"
  />
  <ImageLightbox
    v-if="lightboxTarget"
    :src="lightboxTarget.src"
    :alt="lightboxTarget.alt"
    @close="closeLightbox"
  />
</template>
